import React from 'react';
import { useLingui } from '@lingui/react/macro';

import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import type { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';

import type { LandingSearchFormFilters } from '../../components/LandingSearchForm';
import LandingSearchResultsCarousel from '../../components/LandingSearchResultsCarousel';

import ServiceLandingSection from './ServiceLandingSection';

type Props = {
  serviceTypeName: string;
  providerNounPlural: string;
  searchResults?: OptimizedSearchResponse;
  searchFilters: LandingSearchFormFilters;
  locale: Locale;
  serviceTypeSlug: ServiceTypeSlugEnum;
  countryConfig: Pick<
    CountryConfiguration,
    | 'urls'
    | 'currencyCode'
    | 'hideBackgroundCheckVerbiage'
    | 'formattedVetCareReimbursementAmount'
    | 'bookingFeePercentage'
    | 'bookingFeeCap'
  >;
};

const ServiceLandingSearchResultsCarousel: React.FC<Props> = ({
  serviceTypeName,
  // Aliased to match the interpolation variable name the existing, already-translated
  // "View more {sitterTypePlural}" message was extracted under — a different local name here
  // would generate a new, untranslated message ID for the same copy.
  providerNounPlural: sitterTypePlural,
  searchResults,
  searchFilters,
  locale,
  serviceTypeSlug,
  countryConfig,
}) => {
  const { t } = useLingui();

  if (!searchResults?.results?.length) {
    return null;
  }

  return (
    <ServiceLandingSection>
      <LandingSearchResultsCarousel
        searchResponse={searchResults}
        searchFilters={searchFilters}
        locale={locale}
        heading={t`Highly-rated ${serviceTypeName} in your area`}
        headingVariant="display"
        buttonLabel={t`View more ${sitterTypePlural}`}
        serviceTypeSlug={serviceTypeSlug}
        countryConfig={countryConfig}
      />
    </ServiceLandingSection>
  );
};

export default ServiceLandingSearchResultsCarousel;
